import * as React from "react";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Container from "@amzn/awsui-components-react/polaris/container";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Select from "@amzn/awsui-components-react/polaris/select";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Toggle from "@amzn/awsui-components-react/polaris/toggle";
import Checkbox from "@amzn/awsui-components-react/polaris/checkbox";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import ExpandableSection from "@amzn/awsui-components-react/polaris/expandable-section";
import RadioGroup from "@amzn/awsui-components-react/polaris/radio-group";
import Link from "@amzn/awsui-components-react/polaris/link";
import { API } from "aws-amplify";
import { fetchAllConfigs } from "./cpcp-config-api";
import { getAuthUsername } from "./identity";
import {
  INSURANCE_STATE_VALUES,
  INSURANCE_TYPE_VALUES,
  isDeprecatedValidationType,
  PRESCRIPTION_FILL_STATUS_VALUES,
  PRESCRIPTION_RX_STATUS_VALUES,
} from "./validation-types";
import { COMMON_ALLERGIES, COMMON_HEALTH_CONDITIONS } from '../../../utils/HealthProfileConstants';
import MedicationListSection from "./prescriptions/MedicationListSection";
import NewRxItemsSection from "./prescriptions/NewRxItemsSection";
import { NewRxItemEntry } from "../../update_pharmacy_customer/components/prescription/components/add_prescription_newrx/NewRxItemTypes";
import { buildNewRxBatchItems, validateNewRxItems } from "../../update_pharmacy_customer/components/prescription/components/add_prescription_newrx/NewRxRequestBuilder";

interface PostCreationActionInfo {
  name: string;
  displayName: string;
  description: string;
  tier: string;
  autoResolvedInputs: string[];
  userInputs: string[];
}

interface StepConfig {
  enabled: boolean;
  params: Record<string, string>;
}

interface State {
  step: number;
  featureNames: string[];
  isNewFeature: boolean;
  // Step 1: Identity & Config
  featureName: string;
  customerTypeName: string;
  minimumCustomerTypeCount: string;
  maxCreationCount: string;
  expiryDays: string;
  stepWaitSeconds: string;
  enabled: boolean;
  retrievable: boolean;
  description: string;
  ownerEmail: string;
  forCheckout: boolean;
  forOrder: boolean;
  forPostActions: boolean;
  // Profile-only compositions: a pharmacy customer with no checkout. Each profile layer builds on the one before
  // it (Health Profile on Demographic Profile; Checkout and eScribe on both), so a later layer implies these and
  // includesDemographics()/includesHealth() resolve what the recipe actually includes.
  forDemographicProfile: boolean;
  forHealthProfile: boolean;
  // eScribe / ingested-prescription customer type (mimics the NewRx workflow): creates a
  // health-profile customer with random demographics, then ApaqPortal's PharmacyCustomerLifecycle
  // SFN places the NewRx via UPMS and (if ccsApproval) approves it. Standalone — not combined with
  // checkout/order/post-actions.
  forEscribe: boolean;
  ccsApproval: boolean;
  // Adds a second ADULT with its own pharmacy profile to the same account (addSecondAdultProfile).
  forSecondAdult: boolean;
  // Step 2: Basic Profile + Demographic Profile
  name: string;
  emailPrefix: string;
  phoneNumber: string;
  prime: boolean;
  creditCard: boolean;
  giftCard: boolean;
  giftCardBalance: string;
  rxPassSubscription: boolean;
  rxPassForceActive: boolean;
  hornbillSubscription: boolean;
  customerJourneyType: { label: string; value: string } | null;
  legalFirstName: string;
  middleName: string;
  legalLastName: string;
  suffix: string;
  residentialAddressId: string;
  addresses: Array<{ addressId: string; defaultOption: boolean; state?: string; city?: string; postalCode?: string }>;
  dateOfBirth: string;
  gender: { label: string; value: string } | null;
  ssn: string;
  identityValidationStatus: { label: string; value: string } | null;
  identityValidationForKatara: boolean;
  // Step 3: Health Profile
  spi: string;
  insurancePlan: { label: string; value: string } | null;
  binNumber: string;
  pcn: string;
  groupNumber: string;
  cardHolderId: string;
  setInsuranceAsPrimary: boolean;
  showAdditionalInsurance: boolean;
  binNumber2: string;
  pcn2: string;
  groupNumber2: string;
  cardHolderId2: string;
  setInsuranceAsPrimary2: boolean;
  showAdditionalInsurance2: boolean;
  binNumber3: string;
  pcn3: string;
  groupNumber3: string;
  cardHolderId3: string;
  setInsuranceAsPrimary3: boolean;
  medications: Array<{drugId: string; description: string; defaultOption?: boolean; allowFakeout?: boolean}>;
  medicationList: string[];
  selectedMedication: { label: string; value: string } | null;
  // NewRx (PMGS) batch items for the created customer. Empty = no NewRx. Mutually exclusive with eScribe,
  // and a drugId here must not also appear in medicationList (enforced in handleCreate).
  newRxItems: NewRxItemEntry[];
  newRxActiveTabIndex: number;
  commonAllergies: string[];
  pregnantOrNursing: boolean;
  commonHealthConditions: string[];
  staleHealthProfile: boolean;
  // Step 4: Checkout
  setDefaultAddress: boolean;
  setDefaultPaymentMethod: boolean;
  scriptChaseOption: string;
  // Rx Data Entry (when completeScriptChase)
  quantityAuthorized: string;
  dispenseDaysSupply: string;
  dispenseQuantity: string;
  chaseRenewal: string;
  quantityPerDose: string;
  quantityPerDay: string;
  chaseStatus: string;
  // Step 5: Order
  daysSupply: string;
  initialRefills: string;
  expirationDate: string;
  createOrderType: string;
  createOrderPaymentMethod: string;
  // Step 6 & 7
  postCreationActions: PostCreationActionInfo[];
  validationTypes: string[];
  stepConfigs: Record<string, StepConfig>;
  actionOrder: string[];
  selectedValidations: Record<string, string>;
  insuranceValidation: {
    enabled: boolean;
    insuranceState: string;
    insuranceType: string;
    cardHolderId: string;
    binNumber: string;
    groupNumber: string;
    pcn: string;
  };
  // UI
  loading: boolean;
  saving: boolean;
  error: string | null;
  success: string | null;
}

// The flags that decide which profile layers a recipe includes (see includesDemographics / includesHealth).
type CompositionFlags = Pick<State, 'forDemographicProfile' | 'forHealthProfile' | 'forCheckout' | 'forEscribe'>;

// The composition toggles in order. Each layer builds on the ones before it: Health Profile needs Demographic
// Profile, and Checkout needs both (see setLayer).
const PROFILE_LAYERS = ['forDemographicProfile', 'forHealthProfile', 'forCheckout'] as const;
type ProfileLayer = typeof PROFILE_LAYERS[number];

// Named steps so the visibility logic below reads by name instead of magic numbers (e.g. STEP.DEMOGRAPHICS
// rather than 3). The numeric values are the wizard order; STEP_LABELS/ALL_STEPS derive from these.
const STEP = {
  IDENTITY: 1,
  BASIC_PROFILE: 2,
  DEMOGRAPHICS: 3,
  HEALTH: 4,
  PRESCRIPTIONS: 5,
  CHECKOUT: 6,
  ORDER: 7,
  POST_ACTIONS: 8,
  VALIDATE: 9,
  REVIEW: 10,
} as const;

const ALL_STEPS = Object.values(STEP).sort((a, b) => a - b);
const STEP_LABELS: Record<number, string> = {
  [STEP.IDENTITY]: 'New Type', [STEP.BASIC_PROFILE]: 'Basic Profile', [STEP.DEMOGRAPHICS]: 'Demographics',
  [STEP.HEALTH]: 'Health', [STEP.PRESCRIPTIONS]: 'Prescriptions', [STEP.CHECKOUT]: 'Checkout', [STEP.ORDER]: 'Order',
  [STEP.POST_ACTIONS]: 'Post Actions', [STEP.VALIDATE]: 'Validate', [STEP.REVIEW]: 'Review',
};

const GENDER_OPTIONS = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
];
const VALIDATION_STATUS_OPTIONS = [
  { label: "Success", value: "VALIDATION_SUCCESS" },
  { label: "Failed", value: "VALIDATION_FAILED" },
  { label: "Not Available", value: "VALIDATION_NOT_AVAILABLE" },
];
const PRESCRIBER_OPTIONS = [
  { label: "None", value: "None" },
  { label: "Kristina Bowen, KVH Orthopedics, Ellensburg, WA 98926", value: "1013626948011" },
  { label: "JOHN KIHM, Regional Medical Associates, Durham, NC 27704", value: "6633950178005" },
  { label: "Robert Mackinder, Spectrum Health, Ada, MI 49301", value: "6299122013003" },
  { label: "Gregory Granrud, Healtheast Heart Care, Saint Paul, MN 55102", value: "6261285632002" },
  { label: "DANIEL BROWNFIELD, Corpus Christi, TX 78405", value: "5081356803001" },
  { label: "Gary Schneider, DO, Cincinnati, OH 45219", value: "8208966520001" },
  { label: "Brian Loskill, Charleston, SC 29425", value: "6594995717002" },
  { label: "Sarah Bragg, Birmingham, AL 35235", value: "6809068276002" },
  { label: "Yasmin Deliz, DO, Trenton, NJ 08638", value: "6728920896018" },
  { label: "Cynthia Kang-Rotondo, Knoxville, TN 37916", value: "6013294536008" },
  { label: "James Bertel, Sewell, NJ 08080", value: "6044315631005" },
  { label: "Hugo Twaddle, Bethlehem, PA 18017", value: "6145116342004" },
  { label: "Sarah Carpenter, Kamuela, HI 96743", value: "6794505076010" },
  { label: "Joseph Abayev, Owensboro, KY 42303", value: "8215456027200" },
  { label: "Myna Kuo, Kailua, HI 96734", value: "6582785534001" },
  { label: "Aarti Kulkarni, Beverly Hills, CA 90212", value: "6977486979012" },
];
const INSURANCE_OPTIONS = [
  { label: "Continue Without Insurance", value: "ContinueWithoutInsurance" },
  { label: "Add Insurance Plan", value: "AddInsurancePlan" },
];
const BOOLEAN_VALIDATIONS = ['CUSTOMER_PREFERENCES_EXIST', 'PROFILE_EXISTS'];
const CONTRACT_ENROLLED_OPTIONS = [
  { value: 'AP_PILLPACK', label: 'AP_PILLPACK' },
];
const DISABLED_ACTIONS = ['UPDATE_FILL', 'EXPEDITE_ORDER', 'UPDATE_NBD', 'SHIPMENT_COMPLETE', 'MODIFY_KAMINO_CUSTOMER'];
const PRESCRIPTION_FILL_STATUS_OPTIONS = PRESCRIPTION_FILL_STATUS_VALUES.map(v => ({ value: v, label: v }));

// Prescription-level status for PRESCRIPTION_RX_STATUS (Prescription.getStatus()), used by the
// eScribe type: PRE_PV1 after UPMS ingest, ACTIVE after CCS approval. Distinct from the fill-level
// PRESCRIPTION_FILL_STATUS above.
const PRESCRIPTION_RX_STATUS_OPTIONS = PRESCRIPTION_RX_STATUS_VALUES.map(v => ({ value: v, label: v }));
// Minimum CPCP stepWaitSeconds for the eScribe type: the ApaqPortal lifecycle SFN (UPMS ingest ->
// PRE_PV1 -> optional CCS approve -> ACTIVE) must finish within CPCP's create->validate wait.
const ESCRIBE_MIN_WAIT_SECONDS = 600;

// NewRx (PMGS) recipes need a wait floor so the async ingest/approve flow finishes before CPCP's
// validate step runs, or the provider's partial-batch check marks the pooled customer INVALID.
const NEWRX_MIN_WAIT_SECONDS = 300;
// Preferred Home address for the eScribe type only (Tempe, AZ 85281 — has a valid addressLine2).
// Applied inside toggleEscribe so it doesn't perturb the default address for other recipe types; the
// backend also omits blank addressLine2, so this is belt-and-suspenders, not load-bearing.
const ESCRIBE_PREFERRED_ADDRESS_ZIP = '85281';
const ESCRIBE_PREFERRED_ADDRESS_CITY = 'tempe';

const CHASE_RENEWAL_OPTIONS = [
  { value: "needsrenewal", label: "Yes - Needs Renewal" },
  { value: "thirdparty", label: "No - Third Party" },
  { value: "antibiotic", label: "No - Antibiotic" },
  { value: "short_term_medication", label: "No - Short-term Medication" },
  { value: "taper", label: "No - Taper" },
  { value: "prohibit_renewal_request", label: "No - Refills Not Allowed by Prescriber" },
];

const CHASE_STATUS_OPTIONS = [
  { value: "complete", label: "Complete" },
  { value: "data_entry", label: "Data Entry" },
  { value: "rph_check", label: "RPH Check" },
];

// Insurance validation enums — mirror the CPCP v2 backend allowed values
// (CRISPS InsuranceState / PayerClassification). The INSURANCE_TYPE_CHECK
// validation type is validated by ApaqPortal's ValidateCustomer; these option
// lists drive the Recipe Builder's insurance form.
const INSURANCE_STATE_OPTIONS = INSURANCE_STATE_VALUES.map(v => ({ value: v, label: v }));
const INSURANCE_TYPE_OPTIONS = INSURANCE_TYPE_VALUES.map(v => ({ value: v, label: v }));

const getDefaultState = (): Omit<State, 'featureNames' | 'postCreationActions' | 'validationTypes' | 'stepConfigs' | 'actionOrder' | 'loading' | 'saving' | 'error' | 'success'> => ({
  step: 1,
  isNewFeature: false,
  featureName: '',
  customerTypeName: '',
  minimumCustomerTypeCount: '10',
  maxCreationCount: '5',
  expiryDays: '7',
  stepWaitSeconds: '600',
  enabled: true,
  retrievable: true,
  description: '',
  ownerEmail: '',
  forCheckout: true,
  forOrder: false,
  forPostActions: false,
  forDemographicProfile: false,
  forHealthProfile: false,
  forEscribe: false,
  ccsApproval: false,
  forSecondAdult: false,
  // Step 2: Basic Profile + Demographic
  name: '',
  emailPrefix: '',
  phoneNumber: '',
  prime: false,
  creditCard: true,
  giftCard: false,
  giftCardBalance: '50',
  rxPassSubscription: false,
  rxPassForceActive: false,
  hornbillSubscription: false,
  customerJourneyType: { label: "sign-up-for-self", value: "sign-up-for-self" },
  legalFirstName: '',
  middleName: '',
  legalLastName: '',
  suffix: '',
  residentialAddressId: 'Z5OW3RQYIHYMRINCTJVAG125FA25A8532A372358A52AF52PXTQ2EIA2OXHGTNKL',
  addresses: [],
  dateOfBirth: '2000-01-01',
  gender: { label: "Male", value: "Male" },
  ssn: '',
  identityValidationStatus: { label: "Success", value: "VALIDATION_SUCCESS" },
  identityValidationForKatara: true,
  // Step 3: Health Profile
  spi: '6633950178005',
  insurancePlan: { label: "Continue Without Insurance", value: "ContinueWithoutInsurance" },
  binNumber: '456789',
  pcn: 'XPAY',
  groupNumber: '',
  cardHolderId: '123456789',
  setInsuranceAsPrimary: true,
  showAdditionalInsurance: false,
  binNumber2: '',
  pcn2: '',
  groupNumber2: '',
  cardHolderId2: '',
  setInsuranceAsPrimary2: false,
  showAdditionalInsurance2: false,
  binNumber3: '',
  pcn3: '',
  groupNumber3: '',
  cardHolderId3: '',
  setInsuranceAsPrimary3: false,
  medications: [],
  medicationList: ['5dfe9153bb9b761cd3568361'],
  selectedMedication: null,
  newRxItems: [],
  newRxActiveTabIndex: 0,
  commonAllergies: ['523cbe1aad393f143f002a27'],
  pregnantOrNursing: false,
  commonHealthConditions: ['548b2cc269702d1e63320000'],
  staleHealthProfile: false,
  // Step 4: Checkout
  setDefaultAddress: true,
  setDefaultPaymentMethod: true,
  scriptChaseOption: 'completeScriptChase',
  quantityAuthorized: '30',
  dispenseDaysSupply: '30',
  dispenseQuantity: '30',
  chaseRenewal: 'needsrenewal',
  quantityPerDose: '1',
  quantityPerDay: '1',
  chaseStatus: 'complete',
  // Step 5: Order
  daysSupply: '30',
  initialRefills: '5',
  expirationDate: new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate() - 1).toISOString().slice(0, 10),
  createOrderType: 'cash',
  createOrderPaymentMethod: 'cash',
  selectedValidations: {},
  insuranceValidation: {
    enabled: false,
    insuranceState: '',
    insuranceType: '',
    cardHolderId: '',
    binNumber: '',
    groupNumber: '',
    pcn: '',
  },
});

class RecipeBuilder extends React.Component<{}, State> {
  private topRef = React.createRef<HTMLDivElement>();

  constructor(props: {}) {
    super(props);
    this.state = {
      ...getDefaultState(),
      featureNames: [],
      postCreationActions: [],
      validationTypes: [],
      stepConfigs: {},
      actionOrder: [],
      loading: true,
      saving: false,
      error: null,
      success: null,
    };
  }

  componentDidMount() { this.loadData(); }

  loadData = async () => {
    this.setState({ loading: true, error: null });
    try {
      const [allConfigs, postActionsResult, opsResult, addressResult, medsResult] = await Promise.all([
        fetchAllConfigs<{ featureName: string }>(),
        API.get("ApaqPortalAPI", "/cpcp/list-post-creation-actions", {}),
        API.get("ApaqPortalAPI", "/operations/list", {}),
        API.get("ApaqPortalAPI", "/address/get-addresses", {}).catch(() => ({ addresses: [] })),
        API.get('ApaqPortalAPI', '/drug/get-drugs', {}).catch(() => ({ medications: [] })),
      ]);
      const addresses = (addressResult.addresses || []).map((a: any) => ({ addressId: a.addressId, defaultOption: a.defaultOption, state: a.state, city: a.city, postalCode: a.postalCode }));
      const defaultAddr = addresses.find((a: any) => a.defaultOption);
      const names = [...new Set(allConfigs.map((c: any) => c.featureName))].sort() as string[];
      const postActions: PostCreationActionInfo[] = (postActionsResult.actions || []).map((a: any) => ({
        name: a.name, displayName: a.displayName, description: a.description,
        tier: a.tier, autoResolvedInputs: a.autoResolvedInputs || [], userInputs: a.userInputs || [],
      }));
      const rawOps = opsResult.operations || [];
      const validateOp: any = rawOps.find((o: any) => (typeof o === 'string' ? o : o.name) === 'ValidateCustomer');
      const vtypes: string[] = validateOp?.enumFields?.validations || [];
      const configs: Record<string, StepConfig> = {};
      postActions.forEach(a => { configs[a.name] = { enabled: false, params: {} }; });
      const addrId = defaultAddr ? defaultAddr.addressId : this.state.residentialAddressId;
      this.setState({ featureNames: names, postCreationActions: postActions, validationTypes: vtypes, stepConfigs: configs, addresses, residentialAddressId: addrId, medications: medsResult.medications || [], loading: false });
    } catch (e) {
      this.setState({ error: e instanceof Error ? e.message : "Failed to load data", loading: false });
    }
  };

  // --- helpers for step 7 & 8 ---
  toggleAction = (op: string) => {
    this.setState(prev => {
      const configs = { ...prev.stepConfigs };
      const wasEnabled = configs[op].enabled;
      configs[op] = { ...configs[op], enabled: !wasEnabled };
      // Set default params when enabling
      if (!wasEnabled) {
        if (op === 'PACMAN_ENROLLMENT') configs[op].params = { ...configs[op].params, programName: 'AP_PILLPACK' };
        if (op === 'SCRIPT_CHASE') configs[op].params = { ...configs[op].params, spi: '6633950178005' };
        if (op === 'RPH_DECISION') configs[op].params = { ...configs[op].params, status: 'APPROVED' };
        if (op === 'SET_PRICE_PREFERENCE') configs[op].params = { ...configs[op].params, priceSelectionPreference: 'INSURANCE_PRICE' };
      }
      const order = wasEnabled ? prev.actionOrder.filter(o => o !== op) : [...prev.actionOrder, op];
      return { stepConfigs: configs, actionOrder: order };
    });
  };

  moveAction = (op: string, dir: -1 | 1) => {
    this.setState(prev => {
      const order = [...prev.actionOrder];
      const i = order.indexOf(op);
      if (i < 0 || i + dir < 0 || i + dir >= order.length) return null;
      [order[i], order[i + dir]] = [order[i + dir], order[i]];
      return { actionOrder: order };
    });
  };

  setActionParam = (op: string, field: string, value: string) => {
    this.setState(prev => {
      const configs = { ...prev.stepConfigs };
      const existing = configs[op] || { enabled: false, params: {} };
      configs[op] = { ...existing, params: { ...existing.params, [field]: value } };
      return { stepConfigs: configs };
    });
  };

  // Packets-eligible drugs: the legacy medicationList plus drugId-mode NewRx items (NDC-mode items
  // have no drugId). Shared by renderPostActions() and buildRecipe() so the shown options and the
  // emitted drugIdList stay in sync.
  private packetsEligibleDrugIds = (): Set<string> => {
    const newRxDrugIds = this.state.newRxItems
      .filter((item) => item.drugSource === 'drugId' && item.drugId.trim())
      .map((item) => item.drugId.trim());
    return new Set([...this.state.medicationList, ...newRxDrugIds]);
  };

  // Default "expected" value for a validation type when it's first selected.
  // PRESCRIPTION_RX_STATUS follows the escribe terminal state: ACTIVE when CCS approval is on, else
  // PRE_PV1 (defaulting to PRE_PV1 with approval on would fail validation forever — the customer ends
  // ACTIVE — and silently churn the pool).
  private defaultValidationExpected = (vtype: string, ccsApproval: boolean): string => {
    if (BOOLEAN_VALIDATIONS.includes(vtype)) return 'true';
    switch (vtype) {
      case 'PRESCRIPTION_FILL_STATUS': return 'PURCHASED';
      case 'PRESCRIPTION_RX_STATUS': return ccsApproval ? 'ACTIVE' : 'PRE_PV1';
      case 'CONTRACT_ENROLLED':      return 'AP_PILLPACK';
      case 'INSURANCE_COUNT':        return '1';
      default:                       return '';
    }
  };

  toggleValidation = (vtype: string) => {
    this.setState(prev => {
      const sel = { ...prev.selectedValidations };
      if (sel[vtype] !== undefined) delete sel[vtype];
      else sel[vtype] = this.defaultValidationExpected(vtype, prev.ccsApproval);
      return { selectedValidations: sel };
    });
  };

  setValidationExpected = (vtype: string, value: string) => {
    this.setState(prev => ({ selectedValidations: { ...prev.selectedValidations, [vtype]: value } }));
  };

  setInsuranceValidation = (field: keyof State['insuranceValidation'], value: string) => {
    this.setState(prev => ({ insuranceValidation: { ...prev.insuranceValidation, [field]: value } }));
  };

  // eScribe is a standalone customer type: ApaqPortal's escribe branch runs ahead of
  // checkout/order, so it can't be combined with them. Enabling it clears the other sections and
  // seeds fresh random demographics (name + DOB) — mimics the NewRx workflow, and gives each
  // recipe a distinct patient so UPMS attribution (by name + DOB + address) doesn't collide.
  toggleEscribe = (checked: boolean) => {
    if (checked) {
      // Clear name/DOB (incl. middle/suffix) and let ApaqPortal mint the patient per-customer. CPCP
      // replays one recipe across the whole pool, so any name/DOB baked into the recipe would make
      // every pooled customer identical -> UPMS cross-attribution (it matches by name + DOB + address).
      // The backend's escribe branch fills these when blank. The Demographic step's name/DOB fields are
      // locked for this type (see renderDemographicProfile). Also clear any validations selected under a
      // prior (checkout/order) composition — includeValidate() is true for escribe, so a stale
      // fill-level check like PRESCRIPTION_FILL_STATUS would otherwise leak in and fail the recipe forever.
      this.setState(prev => {
        // Prefer the Tempe address for escribe only (part of the escribe state transition, so it
        // doesn't change the default address for other recipe types). Leave the current address if
        // Tempe isn't in the account's address book (backend omit-blank-addressLine2 covers that case).
        const tempe = prev.addresses.find(a =>
          a.postalCode === ESCRIBE_PREFERRED_ADDRESS_ZIP && (a.city || '').toLowerCase() === ESCRIBE_PREFERRED_ADDRESS_CITY);
        return {
          forEscribe: true,
          forCheckout: false,
          forOrder: false,
          forPostActions: false,
          forDemographicProfile: false,
          forHealthProfile: false,
          legalFirstName: '',
          legalLastName: '',
          middleName: '',
          suffix: '',
          dateOfBirth: '',
          residentialAddressId: tempe ? tempe.addressId : prev.residentialAddressId,
          // Reset the medication list to the default: the field is greyed for escribe, so any meds the
          // user added under a prior (checkout) composition would otherwise be baked into the recipe with
          // no in-UI way to remove them (the × is disabled). Keeps the emitted recipe deterministic.
          medicationList: getDefaultState().medicationList,
          selectedMedication: null,
          // Clear NewRx items too: the editor is disabled under eScribe, so items added beforehand would
          // otherwise be stuck in the recipe with no way to remove them and create permanently blocked.
          newRxItems: [],
          newRxActiveTabIndex: 0,
          selectedValidations: {},
          insuranceValidation: { enabled: false, insuranceState: '', insuranceType: '', cardHolderId: '', binNumber: '', groupNumber: '', pcn: '' },
          // CPCP waits stepWaitSeconds between create and validate; the escribe lifecycle SFN (ingest ->
          // PRE_PV1 -> approve -> ACTIVE) needs comfortably more than a short checkout wait. Bump to the
          // escribe floor if the current value is below it (NaN-safe); the user can still lower it.
          stepWaitSeconds: Number(prev.stepWaitSeconds) >= ESCRIBE_MIN_WAIT_SECONDS ? prev.stepWaitSeconds : String(ESCRIBE_MIN_WAIT_SECONDS),
        };
      });
    } else {
      // Restore the additive-flow defaults (name auto-generated if left empty; default DOB). Clear the
      // escribe-selected validations so an Rx-status check doesn't leak into a non-escribe recipe.
      // Restore the account's default Home address (undo the escribe Tempe preference).
      this.setState(prev => {
        const apiDefault = prev.addresses.find(a => a.defaultOption);
        return {
          forEscribe: false,
          ccsApproval: false,
          forCheckout: true,
          dateOfBirth: '2000-01-01',
          selectedValidations: {},
          residentialAddressId: apiDefault ? apiDefault.addressId : prev.residentialAddressId,
        };
      });
    }
  };

  // Toggling CCS approval changes the escribe flow's terminal state (ACTIVE vs PRE_PV1). If the
  // PRESCRIPTION_RX_STATUS validation is already selected, flip its expected value to match — so the
  // recipe can't be left asserting PRE_PV1 on a customer that ends up ACTIVE (or vice versa), which
  // would fail validation forever.
  setCcsApproval = (checked: boolean) => {
    this.setState(prev => {
      const sel = { ...prev.selectedValidations };
      if (sel.PRESCRIPTION_RX_STATUS !== undefined) {
        sel.PRESCRIPTION_RX_STATUS = checked ? 'ACTIVE' : 'PRE_PV1';
      }
      return { ccsApproval: checked, selectedValidations: sel };
    });
  };

  buildCreateParams = (): Record<string, unknown> => {
    const s = this.state;
    const p: Record<string, unknown> = {};
    // basic profile
    p.name = s.name;
    p.emailPrefix = s.emailPrefix;
    p.phoneNumber = s.phoneNumber;
    p.prime = s.prime;
    p.creditCard = s.creditCard;
    p.giftCard = s.giftCard;
    p.giftCardBalance = Number(s.giftCardBalance) || 50;
    // Basic-profile recipes stop here with exactly the fields the /create-pharmacy-customer
    // "Amazon Customer" call sends (MANUAL source, no demographics/health/checkout/order).
    if (!this.includesDemographics()) {
      p.source = 'MANUAL';
      return p;
    }
    // Every other composition builds on the demographic profile (the fields the /create-pharmacy-customer
    // "Demographic Profile" call sends); health, checkout and order fields are added below.
    p.actorSubType = 'ADULT';
    p.source = 'AUTOMATION';
    p.rxPassSubscription = s.rxPassSubscription;
    p.rxPassForceActive = s.rxPassForceActive;
    p.hornbillSubscription = s.hornbillSubscription;
    // demographics
    p.customerJourneyType = s.customerJourneyType ? s.customerJourneyType.value : 'sign-up-for-self';
    // Blank names are sent as-is; ApaqPortal's applyDemographicDefaults() blank-fills them per-customer
    // because eScribe ingestion requires unique demographics (UPMS attributes on name + DOB + address).
    p.legalFirstName = s.legalFirstName;
    p.middleName = s.forEscribe ? '' : s.middleName;
    p.legalLastName = s.legalLastName;
    p.suffix = s.forEscribe ? '' : s.suffix;
    p.residentialAddressId = s.residentialAddressId;
    p.dateOfBirth = s.forEscribe ? '' : s.dateOfBirth;
    p.gender = s.gender ? s.gender.value : 'Male';
    p.ssn = s.ssn;
    p.identityValidationStatus = s.identityValidationStatus ? s.identityValidationStatus.value : 'VALIDATION_SUCCESS';
    p.identityValidationForKatara = s.identityValidationForKatara;
    p.programName = 'WOLFGANG';
    // ApaqPortal saves the default-address preference on every profile path. eScribe recipes have never sent
    // it, so they keep the preference unset.
    if (!s.forEscribe) {
      p.setDefaultAddress = s.setDefaultAddress;
    }
    if (this.includesSecondAdult()) {
      p.addSecondAdultProfile = true;
    }
    if (!this.includesHealth()) {
      p.demographicProfile = true;
      return p;
    }
    // health
    p.spi = s.spi || '6633950178005';
    if (s.insurancePlan && s.insurancePlan.value === 'AddInsurancePlan') {
      p.addInsurancePlan = true;
      p.insurancePlan = 'Add Insurance Plan';
      p.binNumber = s.binNumber;
      p.pcn = s.pcn;
      p.groupNumber = s.groupNumber;
      p.cardHolderId = s.cardHolderId;
      p.setInsuranceAsPrimary = s.setInsuranceAsPrimary;
      p.binNumber2 = s.binNumber2;
      p.groupNumber2 = s.groupNumber2;
      p.cardHolderId2 = s.cardHolderId2;
      p.pcn2 = s.pcn2;
      p.setInsuranceAsPrimary2 = s.setInsuranceAsPrimary2;
      p.binNumber3 = s.binNumber3;
      p.groupNumber3 = s.groupNumber3;
      p.cardHolderId3 = s.cardHolderId3;
      p.pcn3 = s.pcn3;
      p.setInsuranceAsPrimary3 = s.setInsuranceAsPrimary3;
    } else {
      p.addInsurancePlan = false;
      p.insurancePlan = 'Continue Without Insurance';
      p.binNumber = '';
      p.pcn = '';
      p.groupNumber = '';
      p.cardHolderId = '';
      p.setInsuranceAsPrimary = false;
      p.setInsuranceAsPrimary2 = false;
      p.setInsuranceAsPrimary3 = false;
      p.binNumber2 = '';
      p.binNumber3 = '';
      p.groupNumber2 = '';
      p.groupNumber3 = '';
      p.cardHolderId2 = '';
      p.cardHolderId3 = '';
      p.pcn2 = '';
      p.pcn3 = '';
    }
    p.medicationList = s.medicationList;
    // NewRx (PMGS) batch items — emitted only when present. The CPCP provider maps this JSON array onto
    // List<NewRxBatchItem> on CreatePharmacyCustomerRequest; empty means "no NewRx" (field omitted).
    if (s.newRxItems.length > 0) {
      p.newRxItems = buildNewRxBatchItems(s.newRxItems);
    }
    p.commonAllergies = s.commonAllergies.length > 0 ? s.commonAllergies : ['523cbe1aad393f143f002a27'];
    p.pregnantOrNursing = s.pregnantOrNursing ? 'Yes' : 'No';
    p.commonHealthConditions = s.commonHealthConditions.length > 0 ? s.commonHealthConditions : ['548b2cc269702d1e63320000'];
    p.staleHealthProfile = s.staleHealthProfile;
    p.oneTimeFillReason = '';
    p.status = '';
    // eScribe: create a health-profile customer (demographics + health above), then let ApaqPortal's
    // escribe branch fire the lifecycle SFN (UPMS ingest -> PRE_PV1 -> optional CCS approve -> ACTIVE).
    // The Rx comes from UPMS, NOT scriptChase, so eScribe recipes DON'T emit checkout/order fields.
    // escribeLifeCycle is evaluated first in CreatePharmacyCustomerActivity, ahead of forCheckout.
    if (s.forEscribe) {
      // Subscriptions (Prime/RxPass/Hornbill) aren't part of the eScribe flow and aren't tested with
      // it; zero them so an escribe recipe can't silently carry subscription state left over from a
      // prior composition. (The Basic Profile toggles for RxPass/Hornbill are disabled for eScribe.)
      p.prime = false;
      p.rxPassSubscription = false;
      p.rxPassForceActive = false;
      p.hornbillSubscription = false;
      p.escribeLifeCycle = { ingestPrescription: true, ccsApproval: s.ccsApproval };
      return p;
    }
    if (!s.forCheckout) {
      p.healthProfile = true;
      return p;
    }
    // checkout
    p.forCheckout = s.forCheckout;
    p.setDefaultPaymentMethod = s.setDefaultPaymentMethod;
    p.completeScriptChase = s.scriptChaseOption === 'completeScriptChase';
    p.fakeOutScriptChase = s.scriptChaseOption === 'fakeOutScriptChase';
    p.initiateScriptChase = s.scriptChaseOption === 'initiateScriptChase';
    if (s.scriptChaseOption === 'completeScriptChase') {
      p.quantityAuthorized = s.quantityAuthorized;
      p.dispenseDaysSupply = s.dispenseDaysSupply;
      p.dispenseQuantity = s.dispenseQuantity;
      p.quantityPerDose = s.quantityPerDose;
      p.quantityPerDay = s.quantityPerDay;
      p.chaseRenewal = s.chaseRenewal;
      p.chaseStatus = s.chaseStatus;
    }
    // order
    p.daysSupply = s.daysSupply;
    p.initialRefills = s.initialRefills;
    p.expirationDate = s.expirationDate;
    p.createOrder = s.forOrder;
    p.createOrderType = s.createOrderType;
    p.createOrderPaymentMethod = s.createOrderPaymentMethod;
    return p;
  };

  buildRecipe = (): string => {
    const { stepConfigs, selectedValidations } = this.state;
    const steps: Array<Record<string, unknown>> = [];
    // Step 1: create_pharmacy_customer with all params from steps 2-6
    const createParams = this.buildCreateParams();
    if (Object.keys(createParams).length > 0) {
      steps.push({ action: "create_pharmacy_customer", params: createParams });
    }
    // Post-creation actions as single cpcp_post_action step
    const enabledActions = this.state.actionOrder
      .map((name, idx) => {
        const cfg = stepConfigs[name];
        if (!cfg?.enabled) return null;
        const actionEntry: Record<string, unknown> = { order: idx + 1, actionType: name };
        const params: Record<string, string> = {};
        for (const [field, val] of Object.entries(cfg.params)) {
          if (val !== undefined && val !== '') params[field] = val;
        }
        // Drop drugIds that are no longer packets-eligible: the checkbox UI can't uncheck a drug it no
        // longer renders, so a stale selection would otherwise enrol an ineligible drug.
        if (name === 'PACMAN_ENROLLMENT' && params['drugIdList'] !== undefined) {
          const eligible = this.packetsEligibleDrugIds();
          const pruned = params['drugIdList'].split(',').filter(Boolean).filter(id => eligible.has(id));
          if (pruned.length > 0) params['drugIdList'] = pruned.join(',');
          else delete params['drugIdList'];
        }
        if (Object.keys(params).length > 0) actionEntry.params = params;
        return actionEntry;
      })
      .filter(Boolean);
    // Only emit post-actions when the section is enabled, so deselecting it can't
    // leak stale actions from state (the Post Actions step is hidden when forPostActions is off).
    if (this.state.forPostActions && enabledActions.length > 0) {
      steps.push({ action: "cpcp_post_action", params: { actions: enabledActions } });
    }
    // Step 8: validations
    const validations: Array<Record<string, unknown>> = Object.entries(selectedValidations).map(([vtype, expected]) => {
      const check: Record<string, string> = { type: vtype };
      if (expected) check.expected = expected;
      return check;
    });
    // Insurance validation is performed by ApaqPortal's ValidateCustomer.
    // Emit it as an INSURANCE_TYPE_CHECK type whose expected is an object of
    // the populated fields. The backend matches all fields case-insensitively
    // and trimmed, so no normalization is needed here.
    const iv = this.state.insuranceValidation;
    if (iv.enabled) {
      const expected: Record<string, string> = {};
      if (iv.insuranceState) expected.insuranceState = iv.insuranceState;
      if (iv.insuranceType) expected.insuranceType = iv.insuranceType;
      if (iv.cardHolderId) expected.cardHolderId = iv.cardHolderId;
      if (iv.binNumber) expected.binNumber = iv.binNumber;
      if (iv.groupNumber) expected.groupNumber = iv.groupNumber;
      if (iv.pcn) expected.pcn = iv.pcn;
      validations.push({ type: "INSURANCE_TYPE_CHECK", expected });
    }
    // The Validate step is hidden for a basic-profile-only recipe; don't leak stale
    // validations from state in that case (shares includeValidate() with visibleSteps).
    if (this.includeValidate() && validations.length > 0) {
      steps.push({ action: "validate", params: { validations } });
    }
    const sortKeys = (obj: any): any => {
      if (Array.isArray(obj)) return obj.map(sortKeys);
      if (obj && typeof obj === 'object') {
        const sorted: Record<string, unknown> = {};
        Object.keys(obj).sort().forEach(k => { sorted[k] = sortKeys(obj[k]); });
        return sorted;
      }
      return obj;
    };
    return JSON.stringify(sortKeys({ steps }), null, 2);
  };

  handleCreate = async () => {
    const { featureName, customerTypeName } = this.state;
    if (!featureName || !customerTypeName) {
      this.setState({ error: "Feature Name and Customer Type Name are required" }); return;
    }
    if (!this.state.ownerEmail || !/^[a-zA-Z0-9._-]+@amazon\.com$/.test(this.state.ownerEmail)) {
      this.setState({ error: "A valid Owner Team Email (xxx@amazon.com) is required" }); return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(featureName) || !/^[a-zA-Z0-9_-]+$/.test(customerTypeName)) {
      this.setState({ error: "Feature Name and Customer Type Name must contain only letters, numbers, hyphens, and underscores" }); return;
    }
    if (Number(this.state.minimumCustomerTypeCount) > 200) {
      this.setState({ error: "Minimum Customer Type Count cannot exceed 200" }); return;
    }
    if (Number(this.state.minimumCustomerTypeCount) < 0) {
      this.setState({ error: "Minimum Customer Type Count cannot be negative" }); return;
    }
    if (Number(this.state.maxCreationCount) > 10) {
      this.setState({ error: "Max Creation Count cannot exceed 10" }); return;
    }
    if (Number(this.state.maxCreationCount) < 0) {
      this.setState({ error: "Max Creation Count cannot be negative" }); return;
    }
    if (this.state.insuranceValidation.enabled && !this.state.insuranceValidation.insuranceState) {
      this.setState({ error: "Insurance State is required for insurance validation" }); return;
    }
    if (this.state.forEscribe && !(Number(this.state.stepWaitSeconds) >= ESCRIBE_MIN_WAIT_SECONDS)) {
      this.setState({ error: `Step Wait Seconds must be at least ${ESCRIBE_MIN_WAIT_SECONDS} for eScribe — the lifecycle (ingest → PRE_PV1 → approve → ACTIVE) must finish before CPCP validates` }); return;
    }
    if (this.state.newRxItems.length > 0) {
      const newRxError = this.validateNewRxRecipe();
      if (newRxError) {
        this.setState({ error: newRxError }); return;
      }
    }
    const recipeKey = `${featureName}-${customerTypeName}.json`;
    const content = this.buildRecipe();
    this.setState({ saving: true, error: null, success: null });
    try {
      JSON.parse(content);
      const updatedBy = await getAuthUsername();
      if (!updatedBy) {
        this.setState({ error: "Unable to determine your identity. Please refresh the page and try again.", saving: false });
        return;
      }
      // 1. Create recipe
      await API.put("CpcpV2API", `/cpcp/v2/recipes/${encodeURIComponent(recipeKey)}`, { body: { recipeKey, content, updatedBy } });
      // 2. Create config
      const configBody: Record<string, unknown> = {
        featureName, customerTypeName, recipeKey,
        enabled: this.state.enabled,
        retrievable: this.state.retrievable,
        description: this.state.description,
        ownerEmail: this.state.ownerEmail,
        updatedBy,
      };
      if (this.state.minimumCustomerTypeCount) configBody.minimumCustomerTypeCount = Number(this.state.minimumCustomerTypeCount);
      if (this.state.maxCreationCount) configBody.maxCreationCount = Number(this.state.maxCreationCount);
      if (this.state.expiryDays) configBody.expiryDays = Number(this.state.expiryDays);
      if (this.state.stepWaitSeconds) configBody.stepWaitSeconds = Number(this.state.stepWaitSeconds);
      await API.put("CpcpV2API", `/cpcp/v2/config/${featureName}/${customerTypeName}`, { body: configBody });
      // 3. Reset
      const resetConfigs: Record<string, StepConfig> = {};
      this.state.postCreationActions.forEach(a => { resetConfigs[a.name] = { enabled: false, params: {} }; });
      this.setState({
        ...getDefaultState(),
        stepConfigs: resetConfigs,
        actionOrder: [],
        featureNames: this.state.featureNames.includes(featureName)
          ? this.state.featureNames
          : [...this.state.featureNames, featureName].sort(),
        postCreationActions: this.state.postCreationActions,
        validationTypes: this.state.validationTypes,
        addresses: this.state.addresses,
        medications: this.state.medications,
        loading: false,
        saving: false,
        error: null,
        success: `Recipe "${recipeKey}" and config created successfully!`,
      });
    } catch (e) {
      this.setState({ error: e instanceof Error ? e.message : "Failed to create recipe", saving: false });
    }
  };

  // Profile layers build on each other: Checkout and eScribe customers have a health profile, and a health-profile
  // customer has a demographic profile. Step visibility, the composition toggles and buildCreateParams all read
  // these, so a step can't show for fields the recipe doesn't send (or the reverse). They take the flags as a
  // parameter so setLayer can check the composition it is about to apply.
  includesHealth = (s: CompositionFlags = this.state): boolean => s.forHealthProfile || s.forCheckout || s.forEscribe;
  includesDemographics = (s: CompositionFlags = this.state): boolean => s.forDemographicProfile || this.includesHealth(s);

  // Applies a composition toggle. Turning a layer on also turns on the layers before it; turning it off also turns
  // off the layers after it (and Order with Checkout). The other layers keep what they show. Then drops selections
  // the new composition can't carry: their steps become hidden, so stale NewRx items would block the save and a
  // stale second adult couldn't be turned off.
  setLayer = (layer: ProfileLayer, on: boolean) => {
    this.setState(prev => {
      const shown: Record<ProfileLayer, boolean> = {
        forDemographicProfile: this.includesDemographics(prev),
        forHealthProfile: this.includesHealth(prev),
        forCheckout: prev.forCheckout,
      };
      const at = PROFILE_LAYERS.indexOf(layer);
      const layers = {} as Record<ProfileLayer, boolean>;
      PROFILE_LAYERS.forEach((l, i) => { layers[l] = on ? (i <= at || shown[l]) : (i < at && shown[l]); });
      const next = { ...prev, ...layers };
      const keepHealth = this.includesHealth(next);
      return {
        ...layers,
        forOrder: next.forCheckout && prev.forOrder,
        forSecondAdult: this.includesDemographics(next) && prev.forSecondAdult,
        newRxItems: keepHealth ? prev.newRxItems : [],
        newRxActiveTabIndex: keepHealth ? prev.newRxActiveTabIndex : 0,
      };
    });
  };

  // The Validate step is shown only when at least one optional section is selected; a basic-profile-only
  // recipe has nothing to validate. Single source of truth shared with buildRecipe() so step visibility
  // and recipe emission can't drift.
  includeValidate = (): boolean => this.includesDemographics() || this.state.forOrder || this.state.forPostActions;

  // A second adult joins a main pharmacy profile, which every composition but Basic Profile creates. Shared by
  // the toggle and buildCreateParams so a basic-profile recipe can't carry a stale selection.
  includesSecondAdult = (): boolean => this.state.forSecondAdult && this.includesDemographics();

  // Whether a given wizard step is shown, by name. Identity/Basic Profile/Review are always shown; the
  // rest are gated on the composition:
  //  - Demographics: any type that builds a pharmacy profile (see includesDemographics).
  //  - Health + Prescriptions: any type that builds a health profile (see includesHealth).
  //  - Checkout/Order/Post Actions: their own section toggle. eScribe shows none (its Rx comes from
  //    UPMS, not scriptChase/checkout).
  //  - Validate: whenever there's something to validate (see includeValidate).
  private isStepVisible = (step: number): boolean => {
    const s = this.state;
    switch (step) {
      case STEP.DEMOGRAPHICS:   return this.includesDemographics();
      case STEP.HEALTH:
      case STEP.PRESCRIPTIONS:  return this.includesHealth();
      case STEP.CHECKOUT:     return s.forCheckout;
      case STEP.ORDER:        return s.forOrder;
      case STEP.POST_ACTIONS: return s.forPostActions;
      case STEP.VALIDATE:     return this.includeValidate();
      default:                return true;   // Identity, Basic Profile, Review — always shown
    }
  };

  visibleSteps = (): number[] => ALL_STEPS.filter(s => this.isStepVisible(s));
  displayStep = (): number => this.visibleSteps().indexOf(this.state.step) + 1;
  totalSteps = (): number => this.visibleSteps().length;

  goTo = (step: number) => this.setState({ step, error: null }, () => {
    // Reset scroll on all containers — needed because the APAQ Portal layout uses
    // a nested scrollable container that scrollIntoView/window.scrollTo cannot reach.
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.querySelectorAll('*').forEach(el => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.scrollTop > 0) htmlEl.scrollTop = 0;
    });
  });
  goNext = () => {
    const visible = this.visibleSteps();
    const idx = visible.indexOf(this.state.step);
    if (idx < visible.length - 1) this.goTo(visible[idx + 1]);
  };
  goBack = () => {
    const visible = this.visibleSteps();
    const idx = visible.indexOf(this.state.step);
    if (idx > 0) this.goTo(visible[idx - 1]);
  };
  skipToReview = () => {
    const visible = this.visibleSteps();
    this.goTo(visible[visible.length - 1]);
  };

  // render methods follow...

  renderIdentityConfig = () => {
    const { featureName, customerTypeName, featureNames, isNewFeature } = this.state;
    const featureOptions = [{ label: "+ New Feature Name", value: "__new__" }, ...featureNames.map(f => ({ label: f, value: f }))];
    const recipeKey = featureName && customerTypeName ? `${featureName}-${customerTypeName}.json` : '';
    return (
      <Container header={<Header variant="h3" description="Define the feature name, customer type, and config values">Step {this.displayStep()}: Identity &amp; Config</Header>}>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label={<b>Feature Name</b>} constraintText="Required">
              <Select
                selectedOption={isNewFeature ? { label: "+ New Feature Name", value: "__new__" } : (featureName ? { label: featureName, value: featureName } : null)}
                onChange={({ detail }) => {
                  if (detail.selectedOption.value === "__new__") this.setState({ isNewFeature: true, featureName: '' });
                  else this.setState({ isNewFeature: false, featureName: detail.selectedOption.value || '' });
                }}
                options={featureOptions} placeholder="Select Feature Name"
              />
            </FormField>
            <FormField label={<b>Customer Type Name</b>} constraintText="Required">
              <Input value={customerTypeName} onChange={({ detail }) => this.setState({ customerTypeName: detail.value })} placeholder="e.g., WithInsuranceAndOrder" />
            </FormField>
          </ColumnLayout>
          {isNewFeature && (
            <div style={{ maxWidth: '50%' }}>
            <FormField label={<b>New Feature Name</b>}>
              <Input value={featureName} onChange={({ detail }) => this.setState({ featureName: detail.value })} placeholder="e.g., Manual" />
            </FormField>
            </div>
          )}
          <div style={{ maxWidth: '50%' }}>
          <FormField label={<b>Owner Team Email</b>} constraintText="Required"
            errorText={this.state.ownerEmail && !/^[a-zA-Z0-9._-]+@amazon\.com$/.test(this.state.ownerEmail) ? "Must be a valid @amazon.com email" : undefined}>
            <Input value={this.state.ownerEmail} onChange={({ detail }) => this.setState({ ownerEmail: detail.value })} placeholder="team-alias@amazon.com" />
          </FormField>
          </div>
          {recipeKey && <Box color="text-body-secondary" fontSize="body-s">📄 Recipe key: <b>{recipeKey}</b></Box>}
          <Header variant="h3">Customer Type Composition</Header>
          {/* Additive layers on top of Basic Profile; setLayer keeps them in order. Disabled while the eScribe
              alternate path is on. */}
          <SpaceBetween size="s" direction="horizontal">
            <Toggle checked={true} disabled>Basic Profile</Toggle>
            <Toggle checked={this.includesDemographics()} disabled={this.state.forEscribe} onChange={({ detail }) => this.setLayer('forDemographicProfile', detail.checked)}>Include Demographic Profile</Toggle>
            <Toggle checked={this.includesHealth()} disabled={this.state.forEscribe} onChange={({ detail }) => this.setLayer('forHealthProfile', detail.checked)}>Include Health Profile</Toggle>
            <Toggle checked={this.state.forCheckout} disabled={this.state.forEscribe} onChange={({ detail }) => this.setLayer('forCheckout', detail.checked)}>Include Checkout</Toggle>
            <Toggle checked={this.state.forOrder} disabled={this.state.forEscribe} onChange={({ detail }) => this.setState({ forOrder: detail.checked, forCheckout: detail.checked ? true : this.state.forCheckout })}>Include Order</Toggle>
            <Toggle checked={this.state.forPostActions} disabled={this.state.forEscribe} onChange={({ detail }) => this.setState({ forPostActions: detail.checked })}>Include Post Actions</Toggle>
          </SpaceBetween>
          <Box color="text-body-secondary" fontSize="body-s">
            Health Profile needs Demographic Profile, and Checkout needs both. Turning a layer on also turns on what it needs; turning it off also turns off what needs it.
          </Box>
          {/* Alternate, mutually-exclusive path: eScribe routes through a different backend branch
              (UPMS ingest via the lifecycle SFN) and can't be combined with the additive toggles above. */}
          <Box color="text-body-secondary" fontSize="body-s">— or, an alternate workflow —</Box>
          <SpaceBetween size="s" direction="horizontal">
            <Toggle checked={this.state.forEscribe} onChange={({ detail }) => this.toggleEscribe(detail.checked)}>Ingested Prescription (eScribe)</Toggle>
            {this.state.forEscribe && (
              <Toggle checked={this.state.ccsApproval} onChange={({ detail }) => this.setCcsApproval(detail.checked)}>CCS Approval (→ ACTIVE)</Toggle>
            )}
          </SpaceBetween>
          {this.state.forEscribe && (
            /* Drug/prescriber below are duplicated from the backend's EscribeUpmsEnvelopeBuilder
               constants (DRUG_DESCRIPTION / hardcoded prescriber). If the verified drug ever changes
               there, update this text too. The medication-list sentence relies on toggleEscribe resetting
               medicationList to getDefaultState()'s drugId, which is Atorvastatin 20 MG. */
            <Alert type="info" header="Ingested Prescription (eScribe)">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>
                  Makes a new test patient with a random name and birthday. Then ApaqPortal sends in one
                  prescription for that patient, just like a doctor sending one electronically.
                </li>
                <li>
                  {this.state.ccsApproval
                    ? <>The prescription starts as <b>PRE_PV1</b> (waiting for a pharmacist check). Then it is approved automatically and becomes <b>ACTIVE</b> (ready to fill).</>
                    : <>The prescription stays at <b>PRE_PV1</b> (waiting for a pharmacist check). To have it approved and become <b>ACTIVE</b> (ready to fill), turn on CCS Approval.</>}
                </li>
                <li>
                  The drug is always <b>Atorvastatin 20 MG</b> from Dr. Aarti Kulkarni. You cannot pick a different
                  drug. The medication list is locked to Atorvastatin 20 MG, and NewRx prescriptions cannot be added.
                </li>
              </ul>
            </Alert>
          )}
          <Header variant="h3">Config Values</Header>
          <Alert type="info" header="Resource Limits">
            Min Count is capped at <b>100</b> and Max Create at <b>10</b> per customer type.
            Please configure responsibly — each cached customer consumes test environment resources
            and Step Function executions. Set Min Count to the smallest pool size your team actually needs.
          </Alert>
          <ExpandableSection headerText="Field Glossary">
            <ColumnLayout columns={2}>
              <Box><b>Feature Name</b> — Top-level grouping (e.g., Manual)</Box>
              <Box><b>Customer Type Name</b> — Customer variant (e.g., CustomerWithCheckout)</Box>
              <Box><b>Minimum Customer Type Count</b> — Target pool size: how many READY customers to maintain</Box>
              <Box><b>Max Creation Count</b> — Max customers to create per backfill invocation</Box>
              <Box><b>Enabled</b> — Auto-create customers for this type</Box>
              <Box><b>Retrievable</b> — Whether this customer type can be fetched via GetCachedPharmacyCustomer API</Box>
              <Box><b>Recipe Key</b> — S3 key of the recipe JSON</Box>
              <Box><b>Expiry Days</b> — Days until READY customers expire</Box>
              <Box><b>Step Wait Seconds</b> — Wait between recipe steps</Box>
              <Box><b>Description</b> — Summary of what this customer type includes</Box>
            </ColumnLayout>
          </ExpandableSection>
          <SpaceBetween size="s" direction="horizontal">
            <Toggle checked={this.state.enabled} onChange={({ detail }) => this.setState({ enabled: detail.checked })}>Enabled</Toggle>
            <Toggle checked={this.state.retrievable} onChange={({ detail }) => this.setState({ retrievable: detail.checked })}>Retrievable</Toggle>
          </SpaceBetween>
          <ColumnLayout columns={2}>
            <FormField label={<b>Minimum Customer Type Count</b>} constraintText="Range: 0–200"
              errorText={Number(this.state.minimumCustomerTypeCount) > 200 ? "Must be 200 or less" : Number(this.state.minimumCustomerTypeCount) < 0 ? "Cannot be negative" : undefined}>
              <Input type="number" value={this.state.minimumCustomerTypeCount} onChange={({ detail }) => this.setState({ minimumCustomerTypeCount: detail.value })} />
            </FormField>
            <FormField label={<b>Max Creation Count</b>} constraintText="Range: 0–10"
              errorText={Number(this.state.maxCreationCount) > 10 ? "Must be 10 or less" : Number(this.state.maxCreationCount) < 0 ? "Cannot be negative" : undefined}>
              <Input type="number" value={this.state.maxCreationCount} onChange={({ detail }) => this.setState({ maxCreationCount: detail.value })} />
            </FormField>
            <FormField label={<b>Expiry Days</b>}>
              <Input type="number" value={this.state.expiryDays} onChange={({ detail }) => this.setState({ expiryDays: detail.value })} />
            </FormField>
            <FormField label={<b>Step Wait Seconds</b>}
              errorText={this.state.forEscribe && !(Number(this.state.stepWaitSeconds) >= ESCRIBE_MIN_WAIT_SECONDS)
                ? `eScribe needs at least ${ESCRIBE_MIN_WAIT_SECONDS}s for the lifecycle (ingest → PRE_PV1 → approve → ACTIVE) to finish before CPCP validates`
                : undefined}>
              <Input type="number" value={this.state.stepWaitSeconds} onChange={({ detail }) => this.setState({ stepWaitSeconds: detail.value })} />
            </FormField>
          </ColumnLayout>
          <FormField label={<b>Description</b>}>
            <Textarea value={this.state.description} onChange={({ detail }) => this.setState({ description: detail.value })} rows={2} placeholder="Summary of what this customer type includes" />
          </FormField>
        </SpaceBetween>
      </Container>
    );
  };

  renderBasicProfile = () => {
    const s = this.state;
    // RxPass and Hornbill are created by the demographic, health-profile and checkout paths; eScribe zeroes them.
    const subscriptionsAllowed = this.includesDemographics() && !s.forEscribe;
    return (
      <Container header={<Header variant="h3" description="Basic profile and subscriptions">Step {this.displayStep()}: Basic Profile</Header>}>
        <SpaceBetween size="l">
          <ColumnLayout columns={2}>
            <FormField label={<b>Name (Optional)</b>}>
              <Input value={s.name} onChange={({ detail }) => this.setState({ name: detail.value })} />
            </FormField>
            <FormField label={<b>Email Prefix (Optional)</b>}>
              <Input value={s.emailPrefix} onChange={({ detail }) => this.setState({ emailPrefix: detail.value })} />
            </FormField>
            <FormField label={<b>Phone Number (Optional)</b>}>
              <Input value={s.phoneNumber} onChange={({ detail }) => this.setState({ phoneNumber: detail.value })} />
            </FormField>
          </ColumnLayout>
          <ColumnLayout columns={2}>
            <div><Toggle checked={s.prime} onChange={({ detail }) => {
              const newState: any = { prime: detail.checked };
              if (!detail.checked) { newState.rxPassSubscription = false; newState.rxPassForceActive = false; newState.hornbillSubscription = false; }
              this.setState(newState);
            }}>Add Prime Membership</Toggle></div>
            <div><Toggle checked={s.creditCard} onChange={({ detail }) => this.setState({ creditCard: detail.checked })}>Add Credit Card</Toggle></div>
            <div>
              <Toggle checked={s.giftCard} onChange={({ detail }) => this.setState({ giftCard: detail.checked })}>Add Gift Card</Toggle>
              {s.giftCard && (
                <Box padding={{ top: "xs", left: "l" }}>
                  <FormField label={<b>Gift Card Initial Balance</b>}>
                    <Input type="number" value={s.giftCardBalance} onChange={({ detail }) => this.setState({ giftCardBalance: detail.value })} />
                  </FormField>
                </Box>
              )}
            </div>
            <div>
              <Toggle checked={s.rxPassSubscription} disabled={!s.prime || !subscriptionsAllowed} onChange={({ detail }) => this.setState({ rxPassSubscription: detail.checked })}>Add RxPass Subscription</Toggle>
              {s.rxPassSubscription && (
                <Box padding={{ top: "xs", left: "l" }}>
                  <Toggle checked={s.rxPassForceActive} onChange={({ detail }) => this.setState({ rxPassForceActive: detail.checked })}>Enable RxPass ForceActive</Toggle>
                </Box>
              )}
            </div>
            <div><Toggle checked={s.hornbillSubscription} disabled={!s.prime || !subscriptionsAllowed} onChange={({ detail }) => this.setState({ hornbillSubscription: detail.checked })}>Add Hornbill Subscription</Toggle></div>
          </ColumnLayout>
        </SpaceBetween>
      </Container>
    );
  };

  renderDemographicProfile = () => {
    const s = this.state;
    return (
      <Container header={<Header variant="h3" description="Demographic information">Step {this.displayStep()}: Demographic Profile</Header>}>
        <SpaceBetween size="l">
          <ColumnLayout columns={2}>
            <FormField label={<b>Customer Journey</b>}>
              <Box>sign-up-for-self</Box>
            </FormField>
            <FormField label={<b>Legal first name</b>} constraintText={s.forEscribe ? "Randomized per customer by ApaqPortal (locked for eScribe)" : "Optional — auto-generated if empty"}>
              <Input value={s.legalFirstName} disabled={s.forEscribe} onChange={({ detail }) => this.setState({ legalFirstName: detail.value })} placeholder={s.forEscribe ? "Auto-generated per customer" : undefined} />
            </FormField>
            <FormField label={<b>Middle Name (Optional)</b>} constraintText={s.forEscribe ? "Locked for eScribe" : undefined}>
              <Input value={s.middleName} disabled={s.forEscribe} onChange={({ detail }) => this.setState({ middleName: detail.value })} />
            </FormField>
            <FormField label={<b>Legal Last name</b>} constraintText={s.forEscribe ? "Randomized per customer by ApaqPortal (locked for eScribe)" : "Optional — auto-generated if empty"}>
              <Input value={s.legalLastName} disabled={s.forEscribe} onChange={({ detail }) => this.setState({ legalLastName: detail.value })} placeholder={s.forEscribe ? "Auto-generated per customer" : undefined} />
            </FormField>
            <FormField label={<b>Suffix (Optional)</b>} constraintText={s.forEscribe ? "Locked for eScribe" : undefined}>
              <Input value={s.suffix} disabled={s.forEscribe} onChange={({ detail }) => this.setState({ suffix: detail.value })} />
            </FormField>
            <FormField label={<b>Home Address (Required)</b>}>
              <Select
                selectedOption={s.residentialAddressId ? {
                  label: (() => { const a = s.addresses.find(addr => addr.addressId === s.residentialAddressId); return a ? `${a.state || ''}, ${a.city || ''} ${a.postalCode || ''}` : s.residentialAddressId; })(),
                  value: s.residentialAddressId
                } : null}
                onChange={({ detail }) => this.setState({ residentialAddressId: detail.selectedOption.value || '' })}
                options={s.addresses.length > 0
                  ? s.addresses.map(a => ({ label: `${a.state || ''}, ${a.city || ''} ${a.postalCode || ''}${a.defaultOption ? ' (default)' : ''}`, value: a.addressId }))
                  : [{ label: s.residentialAddressId, value: s.residentialAddressId }]}
                placeholder="Select address"
              />
            </FormField>
            <FormField label={<b>Date of birth</b>} constraintText={s.forEscribe ? "Randomized per customer by ApaqPortal (locked for eScribe)" : "YYYY-MM-DD"}>
              <Input value={s.dateOfBirth} disabled={s.forEscribe} onChange={({ detail }) => this.setState({ dateOfBirth: detail.value })} placeholder={s.forEscribe ? "Auto-generated per customer" : "2000-01-01"} />
            </FormField>
            <FormField label={<b>Gender</b>}>
              <Select selectedOption={s.gender} onChange={({ detail }) => {
                const newState: any = { gender: detail.selectedOption };
                if (detail.selectedOption?.value !== 'Female') newState.pregnantOrNursing = false;
                this.setState(newState);
              }} options={GENDER_OPTIONS} placeholder="Select" />
            </FormField>
            <FormField label={<b>Last 4 of SSN (Optional)</b>}>
              <Input value={s.ssn} onChange={({ detail }) => this.setState({ ssn: detail.value })} />
            </FormField>
            <FormField label={<b>Identity Validation Status (Required)</b>}>
              <Select selectedOption={s.identityValidationStatus} onChange={({ detail }) => this.setState({ identityValidationStatus: detail.selectedOption as any })} options={VALIDATION_STATUS_OPTIONS} />
            </FormField>
          </ColumnLayout>
          {/* eScribe recipes never send setDefaultAddress (see buildCreateParams), so the toggle is hidden for them. */}
          {!s.forEscribe && (
            <Toggle checked={s.setDefaultAddress} onChange={({ detail }) => this.setState({ setDefaultAddress: detail.checked })}>setDefaultAddress</Toggle>
          )}
          <Toggle checked={s.identityValidationForKatara} onChange={({ detail }) => this.setState({ identityValidationForKatara: detail.checked })}>Override Identity Validation Status In Katara</Toggle>
          <Toggle checked={this.includesSecondAdult()} onChange={({ detail }) => this.setState({ forSecondAdult: detail.checked })}>Include Second Adult</Toggle>
          {this.includesSecondAdult() && (
            <Box color="text-body-secondary" fontSize="body-s">
              Adds a second adult with its own pharmacy profile and phone number to the same account.
              Claiming the customer returns it as <b>addedAdultProfile</b>.
            </Box>
          )}
        </SpaceBetween>
      </Container>
    );
  };

  // Medications & prescriptions for the created customer. Split out of the Health step so the legacy
  // medicationList (script-chase / health-profile meds) and the NewRx (PMGS) batch — added in a later
  // task — are configured together and their mutual exclusion is enforced in one place. Gated like
  // Health (see includesHealth). eScribe greys the editors: its Rx is a fixed, verified med.
  // Drug IDs present in both the legacy medicationList and the NewRx items. The backend rejects the
  // overlap, so it drives both the inline warning (renderPrescriptions) and the blocking check
  // (handleCreate) — kept in one place so the two can't diverge.
  private conflictingNewRxDrugIds = (): string[] => {
    const medSet = new Set(this.state.medicationList);
    return Array.from(
      new Set(
        this.state.newRxItems
          .filter((item) => item.drugSource === "drugId" && item.drugId.trim() && medSet.has(item.drugId.trim()))
          .map((item) => item.drugId.trim())
      )
    );
  };

  // Validates the NewRx (PMGS) portion of the recipe, mirroring the Lambda so a bad recipe fails here
  // rather than at provision time. Returns an error message, or null when valid. Only meaningful when
  // newRxItems is non-empty (handleCreate gates on that).
  private validateNewRxRecipe = (): string | null => {
    if (this.state.forEscribe) {
      return "NewRx prescriptions cannot be combined with eScribe — remove the NewRx items or turn off eScribe";
    }
    if (!this.includesHealth()) {
      return "NewRx prescriptions require a health-profile customer — enable Health Profile or Checkout to use them";
    }
    // A NewRx drugId must not also be in the legacy medicationList: the Lambda rejects the overlap
    // (a script-chase Rx for the same drug would collide with batch correlation).
    const overlap = this.conflictingNewRxDrugIds();
    if (overlap.length > 0) {
      return `Drug "${overlap[0]}" is in both the medication list and NewRx prescriptions — remove the duplicate from one of the lists`;
    }
    const itemValidation = validateNewRxItems(this.state.newRxItems);
    if (!itemValidation.valid) {
      return itemValidation.message || "A NewRx prescription is invalid";
    }
    if (!(Number(this.state.stepWaitSeconds) >= NEWRX_MIN_WAIT_SECONDS)) {
      return `Step Wait Seconds must be at least ${NEWRX_MIN_WAIT_SECONDS} for NewRx — the PMGS batch must finish before CPCP validates`;
    }
    // A NewRx recipe must carry a terminal gate: the create step returns once items are *started*, so
    // without a validate step a one-step recipe would reach READY before the async batch completes.
    // Require a PRESCRIPTION_COUNT validation that expects at least one prescription per NewRx item, so a
    // batch that did not actually produce them fails validation instead of handing out an empty customer.
    const expectedCount = Number(this.state.selectedValidations.PRESCRIPTION_COUNT);
    if (!(expectedCount >= this.state.newRxItems.length)) {
      return `NewRx recipes need a Prescription Count validation of at least ${this.state.newRxItems.length} (one per NewRx item) so a customer can't be marked ready before the prescriptions land — add it on the Validate step`;
    }
    return null;
  };

  renderPrescriptions = () => {
    const s = this.state;
    // Drug IDs present in both lists — the backend rejects the overlap, so flag it inline on both sides.
    const conflictingDrugIds = this.conflictingNewRxDrugIds();
    return (
      <SpaceBetween size="l">
        <MedicationListSection
          stepNumber={this.displayStep()}
          medications={s.medications}
          medicationList={s.medicationList}
          selectedMedication={s.selectedMedication}
          disabled={s.forEscribe}
          conflictingDrugIds={conflictingDrugIds}
          onSelectedMedicationChange={(selectedMedication) => this.setState({ selectedMedication })}
          onMedicationListChange={(medicationList) => this.setState({ medicationList })}
        />
        <NewRxItemsSection
          items={s.newRxItems}
          activeTabIndex={s.newRxActiveTabIndex}
          medications={s.medications}
          disabled={s.forEscribe}
          conflictingDrugIds={conflictingDrugIds}
          waitTooLow={s.newRxItems.length > 0 && !(Number(s.stepWaitSeconds) >= NEWRX_MIN_WAIT_SECONDS)}
          minWaitSeconds={NEWRX_MIN_WAIT_SECONDS}
          onItemsChange={(newRxItems) => this.setState({ newRxItems })}
          onActiveTabChange={(newRxActiveTabIndex) => this.setState({ newRxActiveTabIndex })}
        />
      </SpaceBetween>
    );
  };

  renderHealthProfile = () => {
    const s = this.state;
    return (
      <Container header={<Header variant="h3" description="Prescriber, insurance, allergies, and health flags">Step {this.displayStep()}: Health Profile</Header>}>
        <SpaceBetween size="m">
          <FormField label={<b>Prescriber (Optional)</b>}>
            <Select
              expandToViewport
              selectedOption={s.spi ? { label: PRESCRIBER_OPTIONS.find(p => p.value === s.spi)?.label || s.spi, value: s.spi } : null}
              onChange={({ detail }) => this.setState({ spi: detail.selectedOption.value || '' })}
              options={PRESCRIBER_OPTIONS}
              placeholder="Select prescriber"
            />
          </FormField>

          <FormField label={<b>Insurance Plan (Optional)</b>}>
            <Select selectedOption={s.insurancePlan} onChange={({ detail }) => this.setState({ insurancePlan: detail.selectedOption as any })} options={INSURANCE_OPTIONS} />
          </FormField>
          {s.insurancePlan?.value === 'AddInsurancePlan' && (
            <Box padding={{ left: "l" }}>
              <SpaceBetween size="s">
                <ColumnLayout columns={2}>
                  <FormField label={<b>MEMBER ID # (Required)</b>}>
                    <Input value={s.cardHolderId} onChange={({ detail }) => this.setState({ cardHolderId: detail.value })} />
                  </FormField>
                  <FormField label={<b>RXBIN # (Required)</b>}>
                    <Input value={s.binNumber} onChange={({ detail }) => this.setState({ binNumber: detail.value })} />
                  </FormField>
                  <FormField label={<b>RX GROUP # (Optional)</b>}>
                    <Input value={s.groupNumber} onChange={({ detail }) => this.setState({ groupNumber: detail.value })} />
                  </FormField>
                  <FormField label={<b>RXPCN # (Optional)</b>}>
                    <Input value={s.pcn} onChange={({ detail }) => this.setState({ pcn: detail.value })} />
                  </FormField>
                </ColumnLayout>
                <Toggle checked={s.setInsuranceAsPrimary} onChange={({ detail }) => this.setState({ setInsuranceAsPrimary: detail.checked })}>Set Insurance as Primary</Toggle>
                <Toggle checked={s.showAdditionalInsurance} onChange={({ detail }) => {
                  const newState: any = { showAdditionalInsurance: detail.checked };
                  if (!detail.checked) {
                    newState.showAdditionalInsurance2 = false;
                    newState.binNumber2 = ''; newState.pcn2 = ''; newState.groupNumber2 = ''; newState.cardHolderId2 = ''; newState.setInsuranceAsPrimary2 = false;
                  }
                  this.setState(newState);
                }}>Add more insurance</Toggle>
                {s.showAdditionalInsurance && (
                  <Box padding={{ left: "l" }}>
                    <ColumnLayout columns={2}>
                      <FormField label={<b>MEMBER ID # 2</b>}>
                        <Input value={s.cardHolderId2} onChange={({ detail }) => this.setState({ cardHolderId2: detail.value })} />
                      </FormField>
                      <FormField label={<b>RXBIN # 2</b>}>
                        <Input value={s.binNumber2} onChange={({ detail }) => this.setState({ binNumber2: detail.value })} />
                      </FormField>
                      <FormField label={<b>RX GROUP # 2</b>}>
                        <Input value={s.groupNumber2} onChange={({ detail }) => this.setState({ groupNumber2: detail.value })} />
                      </FormField>
                      <FormField label={<b>RXPCN # 2</b>}>
                        <Input value={s.pcn2} onChange={({ detail }) => this.setState({ pcn2: detail.value })} />
                      </FormField>
                    </ColumnLayout>
                    <Toggle checked={s.setInsuranceAsPrimary2} onChange={({ detail }) => this.setState({ setInsuranceAsPrimary2: detail.checked })}>Set Insurance 2 as Primary</Toggle>
                    <Toggle checked={s.showAdditionalInsurance2} onChange={({ detail }) => this.setState({ showAdditionalInsurance2: detail.checked })}>Add more insurance</Toggle>
                    {s.showAdditionalInsurance2 && (
                      <Box padding={{ left: "l" }}>
                        <ColumnLayout columns={2}>
                          <FormField label={<b>MEMBER ID # 3</b>}>
                            <Input value={s.cardHolderId3} onChange={({ detail }) => this.setState({ cardHolderId3: detail.value })} />
                          </FormField>
                          <FormField label={<b>RXBIN # 3</b>}>
                            <Input value={s.binNumber3} onChange={({ detail }) => this.setState({ binNumber3: detail.value })} />
                          </FormField>
                          <FormField label={<b>RX GROUP # 3</b>}>
                            <Input value={s.groupNumber3} onChange={({ detail }) => this.setState({ groupNumber3: detail.value })} />
                          </FormField>
                          <FormField label={<b>RXPCN # 3</b>}>
                            <Input value={s.pcn3} onChange={({ detail }) => this.setState({ pcn3: detail.value })} />
                          </FormField>
                        </ColumnLayout>
                        <Toggle checked={s.setInsuranceAsPrimary3} onChange={({ detail }) => this.setState({ setInsuranceAsPrimary3: detail.checked })}>Set Insurance 3 as Primary</Toggle>
                      </Box>
                    )}
                  </Box>
                )}
              </SpaceBetween>
            </Box>
          )}

          <ColumnLayout columns={2}>
          <FormField label={<b>Common Allergies</b>}>
            <Box padding={{ left: "l" }}>
            <SpaceBetween size="xs">
              {COMMON_ALLERGIES.map(a => (
                <Checkbox key={a.id} checked={s.commonAllergies.includes(a.id)} onChange={({ detail }) => {
                  if (a.name === 'None' && detail.checked) {
                    this.setState({ commonAllergies: [a.id] });
                  } else if (detail.checked) {
                    const noneId = '523cbe1aad393f143f002a27';
                    this.setState({ commonAllergies: [...s.commonAllergies.filter(id => id !== noneId), a.id] });
                  } else {
                    this.setState({ commonAllergies: s.commonAllergies.filter(id => id !== a.id) });
                  }
                }}>{a.name}</Checkbox>
              ))}
            </SpaceBetween>
            </Box>
          </FormField>

          <FormField label={<b>Common Health Conditions</b>}>
            <Box padding={{ left: "l" }}>
            <SpaceBetween size="xs">
              {COMMON_HEALTH_CONDITIONS.map(c => (
                <Checkbox key={c.id} checked={s.commonHealthConditions.includes(c.id)} onChange={({ detail }) => {
                  if (c.name === 'None' && detail.checked) {
                    this.setState({ commonHealthConditions: [c.id] });
                  } else if (detail.checked) {
                    const noneId = '548b2cc269702d1e63320000';
                    this.setState({ commonHealthConditions: [...s.commonHealthConditions.filter(id => id !== noneId), c.id] });
                  } else {
                    this.setState({ commonHealthConditions: s.commonHealthConditions.filter(id => id !== c.id) });
                  }
                }}>{c.name}</Checkbox>
              ))}
            </SpaceBetween>
            </Box>
          </FormField>
          </ColumnLayout>

          {s.gender?.value === 'Female' && (
            <Toggle checked={s.pregnantOrNursing} onChange={({ detail }) => this.setState({ pregnantOrNursing: detail.checked })}>Pregnant or Nursing</Toggle>
          )}

          <Toggle checked={s.staleHealthProfile} onChange={({ detail }) => this.setState({ staleHealthProfile: detail.checked })}>Stale Health Profile</Toggle>
        </SpaceBetween>
      </Container>
    );
  };

  renderCheckout = () => {
    const s = this.state;
    return (
      <Container header={<Header variant="h3" description="Checkout configuration">Step {this.displayStep()}: Checkout</Header>}>
        <SpaceBetween size="m">
          <Toggle checked={s.setDefaultPaymentMethod} onChange={({ detail }) => this.setState({ setDefaultPaymentMethod: detail.checked })}>setDefaultPaymentMethod</Toggle>
          <FormField label={<b>ScriptChase</b>}>
            <RadioGroup
              value={s.scriptChaseOption}
              onChange={({ detail }) => this.setState({ scriptChaseOption: detail.value })}
              items={[
                { value: "initiateScriptChase", label: "Initiate ScriptChase" },
                { value: "fakeOutScriptChase", label: "FakeOut ScriptChase" },
                { value: "completeScriptChase", label: "Complete ScriptChase through PillPack" },
              ]}
            />
          </FormField>
          {s.scriptChaseOption === 'completeScriptChase' && (
            <ExpandableSection headerText="Configure Rx Data Entry" defaultExpanded>
              <ColumnLayout columns={3}>
                <FormField label={<b>Quantity Authorized</b>}>
                  <Input value={s.quantityAuthorized} onChange={({ detail }) => this.setState({ quantityAuthorized: detail.value })} />
                </FormField>
                <FormField label={<b>Dispense Quantity</b>}>
                  <Input value={s.dispenseQuantity} onChange={({ detail }) => this.setState({ dispenseQuantity: detail.value })} />
                </FormField>
                <FormField label={<b>Dispense Days Supply</b>}>
                  <Input value={s.dispenseDaysSupply} onChange={({ detail }) => this.setState({ dispenseDaysSupply: detail.value })} />
                </FormField>
                <FormField label={<b>Chase Renewal</b>}>
                  <Select selectedOption={CHASE_RENEWAL_OPTIONS.find(o => o.value === s.chaseRenewal) || null} onChange={({ detail }) => this.setState({ chaseRenewal: detail.selectedOption.value! })} options={CHASE_RENEWAL_OPTIONS} />
                </FormField>
                <FormField label={<b>Quantity/Dose</b>}>
                  <Select selectedOption={{ label: s.quantityPerDose, value: s.quantityPerDose }} onChange={({ detail }) => this.setState({ quantityPerDose: detail.selectedOption.value! })} options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))} />
                </FormField>
                <FormField label={<b>Quantity/Day</b>}>
                  <Select selectedOption={{ label: s.quantityPerDay, value: s.quantityPerDay }} onChange={({ detail }) => this.setState({ quantityPerDay: detail.selectedOption.value! })} options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))} />
                </FormField>
                <FormField label={<b>Status</b>}>
                  <Select selectedOption={CHASE_STATUS_OPTIONS.find(o => o.value === s.chaseStatus) || null} onChange={({ detail }) => this.setState({ chaseStatus: detail.selectedOption.value! })} options={CHASE_STATUS_OPTIONS} />
                </FormField>
              </ColumnLayout>
            </ExpandableSection>
          )}
        </SpaceBetween>
      </Container>
    );
  };

  renderOrder = () => {
    const s = this.state;
    const hasInsurance = s.insurancePlan?.value === 'AddInsurancePlan';
    const insuranceCount = !hasInsurance ? 0 : s.showAdditionalInsurance2 ? 3 : s.showAdditionalInsurance ? 2 : 1;
    return (
      <Container header={<Header variant="h3" description="Order creation options">Step {this.displayStep()}: Order</Header>}>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label={<b>Order Creation Types</b>}>
              <RadioGroup
                value={s.createOrderType}
                onChange={({ detail }) => this.setState({ createOrderType: detail.value })}
                items={[
                  { value: "cash", label: "Cash Order (no Insurance, not Prime)", disabled: false },
                  { value: "singleInsurance", label: "Single Insurance Order", disabled: insuranceCount < 1 },
                  { value: "multiInsurance", label: "Multi-Insurance Order", disabled: insuranceCount < 2 },
                ]}
              />
            </FormField>
            {s.createOrderType === 'cash' && (
              <FormField label={<b>Order Payment Methods</b>}>
                <RadioGroup
                  value={s.createOrderPaymentMethod}
                  onChange={({ detail }) => this.setState({ createOrderPaymentMethod: detail.value })}
                  items={[
                    { value: "cash", label: "Pay with Credit Card" },
                    { value: "giftCard", label: "Pay with Gift Card" },
                    { value: "hsaCard", label: "Pay with HSA Card" },
                  ]}
                />
              </FormField>
            )}
          </ColumnLayout>
        </SpaceBetween>
      </Container>
    );
  };

  renderPostActions = () => {
    const { postCreationActions, stepConfigs, actionOrder, loading } = this.state;
    if (loading) return <Box textAlign="center" padding="xxl">Loading actions...</Box>;
    return (
      <Container header={<Header variant="h3" description="Select post-creation actions and configure parameters">Step {this.displayStep()}: Post-Creation Actions (Optional)</Header>}>
        <SpaceBetween size="l">
          <FormField label={<b>Available Actions</b>}>
            <SpaceBetween size="xs">
              {postCreationActions.filter(a => !DISABLED_ACTIONS.includes(a.name)).map(action => (
                <Checkbox key={action.name} checked={stepConfigs[action.name]?.enabled || false} onChange={() => this.toggleAction(action.name)}>
                  <b>{action.displayName}</b> — <Box variant="small" display="inline" color="text-body-secondary">{action.description}</Box>
                </Checkbox>
              ))}
            </SpaceBetween>
          </FormField>
          {actionOrder.length > 0 && (
            <FormField label={<b>Selected Actions (execution order)</b>}>
              <SpaceBetween size="s">
                {actionOrder.map((name, idx) => {
                  const action = postCreationActions.find(a => a.name === name);
                  const cfg = stepConfigs[name] || { enabled: false, params: {} };
                  if (!action) return null;
                  return (
                    <div key={name} style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
                      <SpaceBetween size="xs" direction="horizontal">
                        <Box fontSize="heading-m" padding={{ top: "xxs" }}>#{idx + 1}</Box>
                        <Box><b>{action.displayName}</b></Box>
                        {actionOrder.length > 1 && (
                          <SpaceBetween size="xxxs" direction="horizontal">
                            <Button iconName="angle-up" variant="icon" disabled={idx === 0} onClick={() => this.moveAction(name, -1)} />
                            <Button iconName="angle-down" variant="icon" disabled={idx === actionOrder.length - 1} onClick={() => this.moveAction(name, 1)} />
                          </SpaceBetween>
                        )}
                        <Button iconName="close" variant="icon" onClick={() => this.toggleAction(name)} />
                      </SpaceBetween>
                      {action.userInputs.length > 0 && (
                        <Box padding={{ top: "xs", left: "l" }}>
                          <ColumnLayout columns={2}>
                            {action.userInputs.map(field => {
                              if (field === 'drugId' && name === 'ADD_OTC_MEDICATION') {
                                const otcOptions = [
                                  { label: "Melatonin, Tablet, 5 Mg : 5d0959d7bb9b761c5dcfbe73 : BT6SHCY8XM", value: "5d0959d7bb9b761c5dcfbe73" },
                                ];
                                return (
                                  <FormField key={field} label={<b>OTC Medication</b>}>
                                    <Select selectedOption={cfg.params[field] ? otcOptions.find(o => o.value === cfg.params[field]) || null : null}
                                      onChange={({ detail }) => this.setActionParam(name, field, detail.selectedOption.value!)}
                                      options={otcOptions} placeholder="Select OTC medication" />
                                  </FormField>
                                );
                              }
                              if (field === 'drugId') {
                                const medOptions = this.state.medications.map((m: any) => ({ label: m.description || m.name, value: m.drugId }));
                                return (
                                  <FormField key={field} label="Medication">
                                    <Select selectedOption={cfg.params[field] ? medOptions.find((o: any) => o.value === cfg.params[field]) || { label: cfg.params[field], value: cfg.params[field] } : null}
                                      onChange={({ detail }) => this.setActionParam(name, field, detail.selectedOption.value!)}
                                      options={medOptions} placeholder="Select medication" />
                                  </FormField>
                                );
                              }
                              if (field === 'spi') {
                                return (
                                  <FormField key={field} label="Prescriber (SPI)">
                                    <Input value={cfg.params[field] ?? ''} onChange={({ detail }) => this.setActionParam(name, field, detail.value)} />
                                  </FormField>
                                );
                              }
                              if (field === 'programName') {
                                const selectedMeds = this.packetsEligibleDrugIds();
                                const medOptions = this.state.medications
                                  .filter((m: any) => selectedMeds.has(m.drugId))
                                  .map((m: any) => ({ label: m.description || m.name, value: m.drugId }));
                                // Drop stale selections so a now-ineligible drug can't stay checked;
                                // buildRecipe() prunes the emitted drugIdList the same way.
                                const selectedDrugs = (cfg.params['drugIdList'] || '')
                                  .split(',')
                                  .filter(Boolean)
                                  .filter((id: string) => selectedMeds.has(id));
                                return (
                                  <React.Fragment key={field}>
                                    <FormField label={<b>Program Name</b>}>
                                      <Select selectedOption={cfg.params[field] ? { label: cfg.params[field], value: cfg.params[field] } : { label: "AP_PILLPACK", value: "AP_PILLPACK" }}
                                        onChange={({ detail }) => this.setActionParam(name, field, detail.selectedOption.value!)}
                                        options={[{ label: "AP_PILLPACK", value: "AP_PILLPACK" }]} />
                                    </FormField>
                                    <FormField label={<b>Packets-Eligible Medications</b>} constraintText="Select medications to enroll">
                                      {medOptions.map((opt: any) => (
                                        <Checkbox key={opt.value} checked={selectedDrugs.includes(opt.value)}
                                          onChange={({ detail }) => {
                                            const updated = detail.checked ? [...selectedDrugs, opt.value] : selectedDrugs.filter((d: string) => d !== opt.value);
                                            this.setActionParam(name, 'drugIdList', updated.join(','));
                                          }}>{opt.label}</Checkbox>
                                      ))}
                                    </FormField>
                                  </React.Fragment>
                                );
                              }
                              if (field === 'dateShipComplete') {
                                return (
                                  <FormField key={field} label="Date Ship Complete (Optional)">
                                    <input type="datetime-local" value={cfg.params[field] || ''}
                                      onChange={(e) => this.setActionParam(name, field, e.target.value)}
                                      style={{ padding: '8px', borderRadius: '4px', border: '1px solid #aab7b8', width: '100%' }} />
                                  </FormField>
                                );
                              }
                              if (field === 'status' && name === 'RPH_DECISION') {
                                return (
                                  <FormField key={field} label={<b>Decision Status</b>}>
                                    <Select selectedOption={cfg.params[field] ? { label: cfg.params[field], value: cfg.params[field] } : { label: "APPROVED", value: "APPROVED" }}
                                      onChange={({ detail }) => this.setActionParam(name, field, detail.selectedOption.value!)}
                                      options={[{ label: "APPROVED", value: "APPROVED" }]} />
                                  </FormField>
                                );
                              }
                              if (field === 'priceSelectionPreference') {
                                return (
                                  <FormField key={field} label={<b>Price Selection Preference</b>}>
                                    <Select selectedOption={cfg.params[field] ? { label: cfg.params[field], value: cfg.params[field] } : { label: "INSURANCE_PRICE", value: "INSURANCE_PRICE" }}
                                      onChange={({ detail }) => this.setActionParam(name, field, detail.selectedOption.value!)}
                                      options={[
                                        { label: "INSURANCE_PRICE", value: "INSURANCE_PRICE" },
                                        { label: "LOWEST_PRICE", value: "LOWEST_PRICE" },
                                        { label: "PREFER_TO_PAY_WITH_INSURANCE", value: "PREFER_TO_PAY_WITH_INSURANCE" },
                                      ]} />
                                  </FormField>
                                );
                              }
                              return (
                                <FormField key={field} label={field}>
                                  <Input value={cfg.params[field] || ''} onChange={({ detail }) => this.setActionParam(name, field, detail.value)} placeholder={field} />
                                </FormField>
                              );
                            })}
                          </ColumnLayout>
                        </Box>
                      )}
                    </div>
                  );
                })}
              </SpaceBetween>
            </FormField>
          )}
          {postCreationActions.length === 0 && <Box color="text-body-secondary">No post-creation actions available.</Box>}
        </SpaceBetween>
      </Container>
    );
  };

  renderValidation = () => {
    const { validationTypes, selectedValidations, insuranceValidation: iv } = this.state;
    return (
      <Container header={<Header variant="h3" description="Select validations to run after customer creation">Step {this.displayStep()}: Validation (Optional)</Header>}>
        <SpaceBetween size="s">
          {validationTypes.filter(vtype => vtype !== 'INSURANCE_TYPE_CHECK' && !isDeprecatedValidationType(vtype)).map(vtype => (
            <div key={vtype}>
              <Checkbox checked={selectedValidations[vtype] !== undefined} onChange={() => this.toggleValidation(vtype)}>
                <b>{vtype}</b>
              </Checkbox>
              {selectedValidations[vtype] !== undefined && BOOLEAN_VALIDATIONS.includes(vtype) && (
                <Box padding={{ left: "xl", top: "xs" }}>
                  <Toggle checked={selectedValidations[vtype] === 'true'} onChange={({ detail }) => this.setValidationExpected(vtype, detail.checked ? 'true' : 'false')}>
                    Expected: {selectedValidations[vtype]}
                  </Toggle>
                </Box>
              )}
              {selectedValidations[vtype] !== undefined && vtype === 'PRESCRIPTION_FILL_STATUS' && (
                <Box padding={{ left: "xl", top: "xs" }}>
                  <FormField label={<b>Expected fill status</b>}>
                    <Select
                      selectedOption={selectedValidations[vtype] ? { value: selectedValidations[vtype], label: selectedValidations[vtype] } : null}
                      onChange={({ detail }) => this.setValidationExpected(vtype, detail.selectedOption.value!)}
                      options={PRESCRIPTION_FILL_STATUS_OPTIONS}
                      placeholder="Select status"
                    />
                  </FormField>
                </Box>
              )}
              {selectedValidations[vtype] !== undefined && vtype === 'PRESCRIPTION_RX_STATUS' && (
                <Box padding={{ left: "xl", top: "xs" }}>
                  <FormField label={<b>Expected prescription status</b>}>
                    <Select
                      selectedOption={selectedValidations[vtype] ? { value: selectedValidations[vtype], label: selectedValidations[vtype] } : null}
                      onChange={({ detail }) => this.setValidationExpected(vtype, detail.selectedOption.value!)}
                      options={PRESCRIPTION_RX_STATUS_OPTIONS}
                      placeholder="Select status"
                    />
                  </FormField>
                </Box>
              )}
              {selectedValidations[vtype] !== undefined && vtype === 'CONTRACT_ENROLLED' && (
                <Box padding={{ left: "xl", top: "xs" }}>
                  <FormField label={<b>Program name</b>}>
                    <Select
                      selectedOption={selectedValidations[vtype] ? { value: selectedValidations[vtype], label: selectedValidations[vtype] } : null}
                      onChange={({ detail }) => this.setValidationExpected(vtype, detail.selectedOption.value!)}
                      options={CONTRACT_ENROLLED_OPTIONS}
                      placeholder="Select program"
                    />
                  </FormField>
                </Box>
              )}
              {selectedValidations[vtype] !== undefined && !BOOLEAN_VALIDATIONS.includes(vtype) && vtype !== 'PRESCRIPTION_FILL_STATUS' && vtype !== 'PRESCRIPTION_RX_STATUS' && vtype !== 'CONTRACT_ENROLLED' && (
                <Box padding={{ left: "xl", top: "xs" }}>
                  <FormField label={<b>Expected value (optional)</b>}>
                    <Input value={selectedValidations[vtype]} onChange={({ detail }) => this.setValidationExpected(vtype, detail.value)} placeholder="e.g., 1, 2, 3" />
                  </FormField>
                </Box>
              )}
            </div>
          ))}
          {validationTypes.length === 0 && <Box color="text-body-secondary">No backend validation types available.</Box>}

          <div>
            <Checkbox
              checked={iv.enabled}
              onChange={({ detail }) => this.setState({ insuranceValidation: { ...iv, enabled: detail.checked } })}
            >
              <b>INSURANCE_TYPE_CHECK</b>
            </Checkbox>
            {iv.enabled && (
              <Box padding={{ left: "xl", top: "xs" }}>
                <SpaceBetween size="s">
                  <ColumnLayout columns={2}>
                    <FormField label={<b>Insurance State (Required)</b>}>
                      <Select
                        selectedOption={iv.insuranceState ? { label: iv.insuranceState, value: iv.insuranceState } : null}
                        onChange={({ detail }) => this.setInsuranceValidation('insuranceState', detail.selectedOption.value!)}
                        options={INSURANCE_STATE_OPTIONS}
                        placeholder="Select state"
                      />
                    </FormField>
                    <FormField label={<b>Insurance Type (Optional)</b>} constraintText={`Payer Classification: ${INSURANCE_TYPE_VALUES.join(", ")}`}>
                      <Select
                        selectedOption={iv.insuranceType ? { label: iv.insuranceType, value: iv.insuranceType } : null}
                        onChange={({ detail }) => this.setInsuranceValidation('insuranceType', detail.selectedOption.value!)}
                        options={INSURANCE_TYPE_OPTIONS}
                        placeholder="Select type"
                      />
                    </FormField>
                    <FormField label={<b>Member ID (Optional)</b>}>
                      <Input value={iv.cardHolderId} onChange={({ detail }) => this.setInsuranceValidation('cardHolderId', detail.value)} />
                    </FormField>
                    <FormField label={<b>RXBIN (Optional)</b>}>
                      <Input value={iv.binNumber} onChange={({ detail }) => this.setInsuranceValidation('binNumber', detail.value)} />
                    </FormField>
                    <FormField label={<b>RX Group (Optional)</b>}>
                      <Input value={iv.groupNumber} onChange={({ detail }) => this.setInsuranceValidation('groupNumber', detail.value)} />
                    </FormField>
                    <FormField label={<b>RXPCN (Optional)</b>}>
                      <Input value={iv.pcn} onChange={({ detail }) => this.setInsuranceValidation('pcn', detail.value)} />
                    </FormField>
                  </ColumnLayout>
                </SpaceBetween>
              </Box>
            )}
          </div>
        </SpaceBetween>
      </Container>
    );
  };

  // The composition in plain words, for the Review step.
  compositionLabel = (): string => {
    const s = this.state;
    if (s.forEscribe) return 'Ingested Prescription (eScribe)';
    let base = 'Basic Profile';
    if (s.forCheckout) base = 'Checkout';
    else if (s.forHealthProfile) base = 'Health Profile';
    else if (s.forDemographicProfile) base = 'Demographic Profile';
    const layers = [base];
    if (s.forOrder) layers.push('Order');
    if (s.forPostActions) layers.push('Post Actions');
    return layers.join(' + ');
  };

  renderReview = () => {
    const recipe = this.buildRecipe();
    const { featureName, customerTypeName } = this.state;
    const parsed = JSON.parse(recipe);
    const isEmpty = !parsed.steps || parsed.steps.length === 0;
    return (
      <SpaceBetween size="l">
        {isEmpty && <Flashbar items={[{ type: "warning", content: "Recipe has no steps. Go back to add data or actions." }]} />}
        <Container header={<Header variant="h3">Config Summary</Header>}>
          <ColumnLayout columns={2}>
            <Box><b>Feature Name:</b> {featureName}</Box>
            <Box><b>Customer Type:</b> {customerTypeName}</Box>
            <Box><b>Composition:</b> {this.compositionLabel()}</Box>
            <Box><b>Recipe Key:</b> {featureName}-{customerTypeName}.json</Box>
            <Box><b>Enabled:</b> {this.state.enabled ? 'Yes' : 'No'}</Box>
            <Box><b>Retrievable:</b> {this.state.retrievable ? 'Yes' : 'No'}</Box>
            <Box><b>Expiry Days:</b> {this.state.expiryDays}</Box>
            <Box><b>Step Wait Seconds:</b> {this.state.stepWaitSeconds}</Box>
            {this.state.minimumCustomerTypeCount && <Box><b>Min Count:</b> {this.state.minimumCustomerTypeCount}</Box>}
            {this.state.maxCreationCount && <Box><b>Max Creation:</b> {this.state.maxCreationCount}</Box>}
            {this.state.description && <Box><b>Description:</b> {this.state.description}</Box>}
            {this.state.ownerEmail && <Box><b>Owner Email:</b> {this.state.ownerEmail}</Box>}
          </ColumnLayout>
        </Container>
        {this.renderPrescriptionsSummary()}
        <Container header={<Header variant="h3">Recipe JSON</Header>}>
          <Textarea value={recipe} readOnly rows={Math.min(30, recipe.split('\n').length + 2)} />
        </Container>
      </SpaceBetween>
    );
  };

  // Summarizes the two prescription-ingestion lists on the Review step. Shown only when the Medications
  // step is in play and at least one list has entries; the full Recipe JSON above already carries both.
  private renderPrescriptionsSummary = () => {
    const s = this.state;
    if (!this.includesHealth()) return null;
    if (s.medicationList.length === 0 && s.newRxItems.length === 0) return null;
    const drugLabel = (drugId: string) => {
      const med = s.medications.find((m) => m.drugId === drugId);
      return med ? med.description : drugId;
    };
    return (
      <Container header={<Header variant="h3">Prescriptions Summary</Header>}>
        <SpaceBetween size="m">
          <Box>
            <b>Medication list (legacy):</b>{" "}
            {s.medicationList.length > 0 ? s.medicationList.map(drugLabel).join(", ") : "None"}
          </Box>
          <Box>
            <b>NewRx prescriptions (PMGS):</b>{" "}
            {s.newRxItems.length > 0
              ? s.newRxItems
                  .map((item, i) => {
                    const drug =
                      item.drugSource === "drugId"
                        ? item.drugId
                          ? drugLabel(item.drugId)
                          : "(no drug selected)"
                        : `NDC ${item.medication.ndc || "(none)"}`;
                    return `${i + 1}. ${drug} [${item.stopAtStep}]`;
                  })
                  .join("; ")
              : "None"}
          </Box>
        </SpaceBetween>
      </Container>
    );
  };

  renderNav = () => {
    const { step, saving, featureName, customerTypeName } = this.state;
    const canProceedStep1 = !!(featureName && customerTypeName
        && this.state.ownerEmail && /^[a-zA-Z0-9._-]+@amazon\.com$/.test(this.state.ownerEmail)
        && Number(this.state.minimumCustomerTypeCount) >= 0
        && Number(this.state.minimumCustomerTypeCount) <= 200
        && Number(this.state.maxCreationCount) >= 0
        && Number(this.state.maxCreationCount) <= 10);
    const isOptional = step === STEP.POST_ACTIONS;
    const lastStep = this.visibleSteps()[this.totalSteps() - 1];
    return (
      <Box float="right" padding={{ top: "m" }}>
        <SpaceBetween size="xs" direction="horizontal">
          <Box color="text-body-secondary" padding={{ top: "xs" }}>Step {this.displayStep()} of {this.totalSteps()}</Box>
          {step > STEP.IDENTITY && <Button onClick={this.goBack}>Back</Button>}
          {isOptional && <Button onClick={this.skipToReview}>Skip to Review</Button>}
          {step < lastStep && (
            <Button variant="primary" disabled={step === STEP.IDENTITY && !canProceedStep1} onClick={this.goNext}>Next</Button>
          )}
          {step === lastStep && (
            <Button variant="primary" onClick={this.handleCreate} loading={saving}>Create Recipe</Button>
          )}
        </SpaceBetween>
      </Box>
    );
  };

  render() {
    const { step, error, success } = this.state;
    return (
      <div ref={this.topRef}>
      <SpaceBetween size="l">
        <Header
          variant="h2"
          description={
            <Link href="https://quip-amazon.com/HHYaAndw9DtH" external>
              How to use the Recipe Builder
            </Link>
          }
        >
          Recipe Builder
        </Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', padding: '8px 0' }}>
          {this.visibleSteps().map((s, idx) => {
            const isActive = s === step;
            const isCompleted = this.visibleSteps().indexOf(s) < this.visibleSteps().indexOf(step);
            return (
              <React.Fragment key={s}>
                {idx > 0 && <span style={{ color: '#aab7b8', margin: '0 2px' }}>→</span>}
                <button
                  type="button"
                  onClick={() => this.goTo(s)}
                  aria-current={isActive ? 'step' : undefined}
                  style={{
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: isActive ? 600 : 400,
                    background: isActive ? '#0972d3' : isCompleted ? '#e9f3fe' : '#f2f3f3',
                    color: isActive ? '#fff' : isCompleted ? '#0972d3' : '#545b64',
                  }}
                >{STEP_LABELS[s]}</button>
              </React.Fragment>
            );
          })}
        </div>
        {error && <Flashbar items={[{ type: "error", content: error, dismissible: true, onDismiss: () => this.setState({ error: null }) }]} />}
        {success && <Flashbar items={[{ type: "success", content: success, dismissible: true, onDismiss: () => this.setState({ success: null }) }]} />}
        {step === STEP.IDENTITY && this.renderIdentityConfig()}
        {step === STEP.BASIC_PROFILE && this.renderBasicProfile()}
        {step === STEP.DEMOGRAPHICS && this.renderDemographicProfile()}
        {step === STEP.HEALTH && this.renderHealthProfile()}
        {step === STEP.PRESCRIPTIONS && this.renderPrescriptions()}
        {step === STEP.CHECKOUT && this.renderCheckout()}
        {step === STEP.ORDER && this.renderOrder()}
        {step === STEP.POST_ACTIONS && this.renderPostActions()}
        {step === STEP.VALIDATE && this.renderValidation()}
        {step === STEP.REVIEW && this.renderReview()}
        {this.renderNav()}
      </SpaceBetween>
      </div>
    );
  }
}

export default RecipeBuilder;
